import { API } from "@swivl-mono/api-helper";
import { PhoneNumberHelper } from "@swivl-mono/helpers";
import { CallHistoryItem, CallRecordingResponse } from "@swivl-mono/types";
import { InsetTabsComponent, Panel } from "@swivl-mono/ui";
import React, { useEffect, useState } from "react";
import { TbExternalLink } from "react-icons/tb";
import { Link, To } from "react-router-dom";

import { ConversationIcon } from "../../../../../Components/Icons/ConversationIcon";
import { formatDuration } from "../../../../../Helpers/Time/formatDuration";
import { RecordingPlayer } from "../RecordingPlayer/RecordingPlayer";

interface RecordingPanelProps {
  recording: CallRecordingResponse;
  onClose: () => void;
}

const tabs = [{ id: "recording", label: "Recording", badge: false }];

const shouldIgnoreOutsideClick = (event: MouseEvent) => {
  const target = event.target as Node;
  if (target instanceof Element) {
    return Boolean(target.closest('[role="menu"]')) || Boolean(target.closest("tbody"));
  }
  return false;
};

const RecordingPanel: React.FC<RecordingPanelProps> = ({ recording, onClose }) => {
  const [detail, setDetail] = useState<CallRecordingResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!recording.callSid) {
      setDetail(null);
      setDetailError(undefined);
      return;
    }

    let cancelled = false;
    setDetail(null);
    setDetailError(undefined);
    setIsLoadingDetail(true);

    void (async () => {
      try {
        const data = await API.Calls.getCallRecordingByCallSid(recording.callSid);
        if (!cancelled) {
          setDetail(data ?? null);
          if (!data) setDetailError("Recording not found");
        }
      } catch (e) {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : "Failed to load recording");
        }
      } finally {
        if (!cancelled) setIsLoadingDetail(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [recording.callSid]);

  const display = detail ?? recording;
  const history: CallHistoryItem[] = detail?.history ?? [];

  return (
    <Panel isOpen={true} onToggle={onClose} width="400px" shouldIgnoreOutsideClick={shouldIgnoreOutsideClick}>
      <div className="flex h-full flex-col gap-3 bg-grey-50 px-3 pb-3">
        <div className="flex items-center justify-between">
          <InsetTabsComponent items={tabs} selectedItem={tabs[0].id} itemSelected={() => {}} />
        </div>
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
          <div className="rounded-sm border border-gray-300 bg-white p-3">
            <div className="flex flex-col gap-2">
              <div className="inline-flex flex-col items-start justify-start gap-2 font-sans">
                <Link
                  to={`/crm?userId=${display.userId}`}
                  target="_blank"
                  className="inline-flex items-center justify-start gap-1 underline hover:no-underline"
                >
                  <div className="justify-start text-center font-['Poppins'] text-base leading-5 font-semibold text-black">{display.userName}</div>
                  <TbExternalLink className="h-4 w-4" />
                </Link>
                <div className="justify-start text-center text-sm text-gray-900">{PhoneNumberHelper.formatPhoneNumber(display.userPhoneNumber)}</div>
              </div>
              <hr className="border-gray-300" />
              <Link
                to={`/contact-center/conversations/user/${display.userId}?channel=Voice` as To}
                className="flex items-center justify-start gap-1 font-sans text-orange-500 hover:text-orange-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ConversationIcon />
                <div className="text-sm font-semibold">Conversation</div>
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-sm border border-gray-300 bg-white p-3">
            <RecordingPlayer audioSrc={display.recordingUrl} />

            <hr className="border-gray-300" />

            <div className="flex flex-col gap-1">
              <div className="body-sm-alt text-grey-900">Event History</div>
              {detailError && <div className="text-sm text-red-600">{detailError}</div>}
              {isLoadingDetail && !detail && <div className="font-mono text-sm text-gray-500">Loading…</div>}
              {!isLoadingDetail && !detailError && (
                <div className="flex flex-col gap-1">
                  {history.length === 0 ? (
                    <div className="font-mono text-sm text-gray-500">No events</div>
                  ) : (
                    history.map((event, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="body-mono text-grey-600">{event.description}</div>
                        <div className="body-mono-alt text-grey-900">{formatDuration(event.timeSeconds)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/*<Button variant={ButtonVariant.PRIMARY} color={ButtonColor.BLACK} size={ButtonSize.MEDIUM} leftIcon={<TbSparkles className="h-4 w-4" />}>*/}
          {/*  Generate Summary*/}
          {/*</Button>*/}
        </div>
      </div>
    </Panel>
  );
};

export default RecordingPanel;
